import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, CreditCard, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api, customerAuthHeaders } from '../lib/api';
import { isCustomerAuthenticated } from '../lib/session';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

export default function TourBookingPanel({ page, slug }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [preferredStartDate, setPreferredStartDate] = useState('');
  const [participants, setParticipants] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAuthenticated = isCustomerAuthenticated();
  const title = i18n.language === 'en' && page?.title_en ? page.title_en : page?.title;
  const isTourPage = slug?.startsWith('tours-');
  const depositAmount = page?.deposit_amount ?? null;
  const finalAmount = page?.final_amount ?? null;
  const currentPath = location.pathname;

  const labels = useMemo(() => (
    i18n.language === 'en'
      ? {
        heading: 'Book This Tour',
        desc: 'Create a booking in your account and track deposit and final payment statuses.',
        auth: 'Sign in or register to book this tour',
        authButton: 'Open Account',
        date: 'Preferred start date',
        participants: 'Participants',
        notes: 'Notes',
        notesPlaceholder: 'Dates, preferences, room options, dietary requests, visa questions...',
        submit: 'Create Booking',
        deposit: 'Deposit',
        final: 'Final payment',
        pending: 'Amount will be confirmed by manager',
      }
      : {
        heading: 'Забронировать тур',
        desc: 'Создайте бронь в личном кабинете и отслеживайте статус депозита и полной оплаты.',
        auth: 'Войдите или зарегистрируйтесь, чтобы забронировать этот тур',
        authButton: 'Открыть кабинет',
        date: 'Предпочтительная дата старта',
        participants: 'Количество участников',
        notes: 'Комментарий',
        notesPlaceholder: 'Даты, пожелания, номера, питание, визовые вопросы...',
        submit: 'Создать бронь',
        deposit: 'Депозит',
        final: 'Полная оплата',
        pending: 'Сумму подтвердит менеджер',
      }
  ), [i18n.language]);

  if (!isTourPage) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) {
      navigate(`/account/auth?next=${encodeURIComponent(currentPath)}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/customer/bookings', {
        tour_slug: slug,
        preferred_start_date: preferredStartDate || null,
        participants: Number(participants) || 1,
        notes: notes || null,
      }, customerAuthHeaders());
      toast.success(i18n.language === 'en' ? 'Booking created' : 'Бронь создана');
      navigate('/account');
    } catch (error) {
      toast.error(error?.response?.data?.detail || (i18n.language === 'en' ? 'Failed to create booking' : 'Не удалось создать бронь'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 mt-12">
      <div className="glass rounded-3xl border border-white/10 p-6 md:p-8">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
              <CreditCard size={14} />
              <span>{title}</span>
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-black text-white">{labels.heading}</h3>
            <p className="text-white/70 max-w-2xl">{labels.desc}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-white/50 text-sm mb-1">{labels.deposit}</div>
                <div className="text-white text-xl font-bold">{depositAmount ? `${depositAmount} RUB` : labels.pending}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-white/50 text-sm mb-1">{labels.final}</div>
                <div className="text-white text-xl font-bold">{finalAmount ? `${finalAmount} RUB` : labels.pending}</div>
              </div>
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="rounded-2xl border border-white/10 bg-black/30 p-6 space-y-4">
              <p className="text-white/70">{labels.auth}</p>
              <Button asChild className="bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full px-6 py-3">
                <Link to={`/account/auth?next=${encodeURIComponent(currentPath)}`}>{labels.authButton}</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-black/30 p-6 space-y-4">
              <div>
                <Label className="text-white mb-2 block">{labels.date}</Label>
                <div className="relative">
                  <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                  <Input type="date" value={preferredStartDate} onChange={(event) => setPreferredStartDate(event.target.value)} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">{labels.participants}</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                  <Input type="number" min="1" max="50" value={participants} onChange={(event) => setParticipants(event.target.value)} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">{labels.notes}</Label>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  className="w-full min-h-[120px] rounded-xl border border-white/10 bg-zinc-900/50 px-4 py-3 text-white"
                  placeholder={labels.notesPlaceholder}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full py-3">
                {submitting ? '...' : labels.submit}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
